// src/pages/Admin/DescuentosAutomaticos/DescuentoAutomaticoForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Card, Form, Input, Select, DatePicker, InputNumber, Switch, Button,
  Space, Typography, Divider, Alert, Tag, Radio, Spin, App, Slider
} from 'antd';
import {
  SaveOutlined, ArrowLeftOutlined, PercentageOutlined, DollarOutlined,
  InfoCircleOutlined, CheckCircleOutlined, AppstoreOutlined,
  ThunderboltOutlined, CalendarOutlined, TrophyOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import DescuentosAutomaticos from '../../../../../service/CodigosDescuento';
import Talleres from '../../../../../service/Talleres';
import { useNotify, useWithNotify } from '../../../../../providers/NotificationProvider';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

// Formato de moneda CLP
const formatCLP = (value) => {
  if (!value) return '';
  return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const parseCLP = (value) => {
  if (!value) return undefined;
  return Number(value.replace(/\./g, ''));
};

const DescuentoAutomaticoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const withNotify = useWithNotify();
  const { modal } = App.useApp();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [talleres, setTalleres] = useState([]);
  const [talleresLoading, setTalleresLoading] = useState(false);

  // Estados del formulario
  const [tipoDescuento, setTipoDescuento] = useState('porcentaje');
  const [prioridad, setPrioridad] = useState(0);

  const isEditMode = !!id;

  // 🔧 CORREGIDO: Cargar TODOS los talleres para el select
  const loadTalleres = async () => {
    try {
      setTalleresLoading(true);
      // ✅ Usar allTalleres=true para obtener todos los talleres sin paginación
      const res = await Talleres.List({ allTalleres: true });      
      setTalleres(res.talleres || []);
    } catch (error) {
      notify.error('Error al cargar talleres');
      setTalleres([]);
    } finally {
      setTalleresLoading(false);
    }
  };

  // Cargar descuento si es edición
  const loadDescuento = async () => {
    try {
      setLoading(true);
      const res = await DescuentosAutomaticos.Get(id);
      const descuento = res;

      // Setear valores del formulario
      form.setFieldsValue({
        nombre: descuento.nombre,
        descripcion: descuento.descripcion,
        tipoDescuento: descuento.tipoDescuento,
        valorDescuento: descuento.valorDescuento,
        fechas: [dayjs(descuento.fechaInicio), dayjs(descuento.fechaFin)],
        talleresAplicables: descuento.talleresAplicables?.map(t => t._id || t),
        prioridad: descuento.prioridad || 0,
        acumulableConOtrosDescuentos: descuento.acumulableConOtrosDescuentos || false,
        activo: descuento.activo,
      });

      // Setear estados
      setTipoDescuento(descuento.tipoDescuento);
      setPrioridad(descuento.prioridad || 0);
    } catch (error) {
      console.error('Error cargando descuento:', error);
      notify.error('Error al cargar descuento automático');
      navigate('/admin/descuentos-automaticos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTalleres();
    if (isEditMode) {
      loadDescuento();
    }
  }, [id]);

  // Guardar descuento
  const handleSubmit = async (values) => {
    try {
      setSaving(true);

      // Validar fechas
      const [fechaInicio, fechaFin] = values.fechas;
      if (dayjs(fechaInicio).isAfter(dayjs(fechaFin))) {
        notify.error('La fecha de inicio debe ser anterior a la fecha de fin');
        return;
      }

      // Validar que tenga talleres seleccionados
      if (!values.talleresAplicables || values.talleresAplicables.length === 0) {
        notify.error('Debe seleccionar al menos un taller');
        return;
      }

      // Construir payload
      const payload = {
        nombre: values.nombre.trim(),
        descripcion: values.descripcion.trim(),
        tipoDescuento: values.tipoDescuento,
        valorDescuento: values.valorDescuento,
        talleresAplicables: values.talleresAplicables,
        fechaInicio: dayjs(fechaInicio).toISOString(),
        fechaFin: dayjs(fechaFin).toISOString(),
        prioridad: values.prioridad || 0,
        acumulableConOtrosDescuentos: values.acumulableConOtrosDescuentos || false,
        activo: values.activo !== undefined ? values.activo : true,
      };

      // Validaciones adicionales
      if (payload.tipoDescuento === 'porcentaje' && (payload.valorDescuento <= 0 || payload.valorDescuento > 100)) {
        notify.error('El porcentaje de descuento debe estar entre 1 y 100%');
        return;
      }

      if (payload.tipoDescuento === 'monto_fijo' && payload.valorDescuento <= 0) {
        notify.error('El monto de descuento debe ser mayor a 0');
        return;
      }

      // Crear o actualizar
      if (isEditMode) {
        await withNotify(
          DescuentosAutomaticos.Update(id, payload),
          {
            loading: 'Actualizando descuento...',
            success: 'Descuento actualizado exitosamente',
            error: 'Error al actualizar descuento'
          }
        );
      } else {
        await withNotify(
          DescuentosAutomaticos.Create(payload),
          {
            loading: 'Creando descuento...',
            success: 'Descuento creado exitosamente',
            error: 'Error al crear descuento'
          }
        );
      }

      navigate('/admin/descuentos-automaticos');
    } catch (error) {
      console.error('Error guardando descuento:', error);
      notify.error(error.message || 'Error al guardar descuento');
    } finally {
      setSaving(false);
    }
  };

  // Cancelar
  const handleCancel = () => {
    modal.confirm({
      title: '¿Descartar cambios?',
      content: 'Se perderán todos los cambios no guardados.',
      okText: 'Descartar',
      okType: 'danger',
      cancelText: 'Continuar editando',
      onOk: () => navigate('/admin/descuentos-automaticos'),
    });
  };

  // Marca de prioridad
  const prioridadMarks = {
    0: 'Baja',
    5: 'Media',
    10: 'Alta'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" tip="Cargando descuento automático..." />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <Card className="shadow-lg rounded-lg max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/admin/descuentos-automaticos')}
            className="mb-4"
          >
            Volver
          </Button>
          <Title level={3} className="!mb-2 flex items-center gap-2">
            <ThunderboltOutlined className="text-yellow-500" />
            {isEditMode ? 'Editar Descuento Automático' : 'Crear Descuento Automático'}
          </Title>
          <Text type="secondary">
            {isEditMode 
              ? 'Modifica los datos del descuento automático'
              : 'Completa los datos para crear un nuevo descuento que se aplicará automáticamente'}
          </Text>
        </div>

        {/* Alert informativo */}
        <Alert
          message="Descuentos Automáticos"
          description="Los descuentos automáticos se aplican sin necesidad de código durante el período configurado. Se activan automáticamente en el carrito y checkout para los talleres seleccionados."
          type="info"
          icon={<ThunderboltOutlined />}
          showIcon
          className="mb-6"
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            activo: true,
            tipoDescuento: 'porcentaje',
            prioridad: 0,
            acumulableConOtrosDescuentos: false,
          }}
        >
          {/* Información básica */}
          <Card title="Información Básica" size="small" className="mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label="Nombre del Descuento"
                name="nombre"
                rules={[
                  { required: true, message: 'Ingrese el nombre del descuento' },
                  { min: 5, message: 'Mínimo 5 caracteres' },
                  { max: 100, message: 'Máximo 100 caracteres' }
                ]}
                tooltip="Nombre identificatorio del descuento (ej: Descuento Verano 2025)"
              >
                <Input
                  placeholder="Descuento Verano 2025"
                  maxLength={100}
                />
              </Form.Item>

              <Form.Item
                label="Estado"
                name="activo"
                valuePropName="checked"
                tooltip="El descuento solo se aplicará si está activo y dentro del período de vigencia"
              >
                <Switch
                  checkedChildren="Activo"
                  unCheckedChildren="Inactivo"
                />
              </Form.Item>
            </div>

            <Form.Item
              label="Descripción"
              name="descripcion"
              rules={[
                { required: true, message: 'Ingrese una descripción' },
                { min: 10, message: 'Mínimo 10 caracteres' },
                { max: 250, message: 'Máximo 250 caracteres' }
              ]}
            >
              <TextArea
                rows={3}
                placeholder="Ej: Descuento especial para talleres de natación durante la temporada de verano 2025"
                maxLength={250}
                showCount
              />
            </Form.Item>

          </Card>

          {/* Tipo y valor de descuento */}
          <Card title="Configuración del Descuento" size="small" className="mb-4">
            <Form.Item
              label="Tipo de Descuento"
              name="tipoDescuento"
              rules={[{ required: true }]}
            >
              <Radio.Group
                onChange={(e) => setTipoDescuento(e.target.value)}
                buttonStyle="solid"
              >
                <Radio.Button value="porcentaje">
                  <PercentageOutlined /> Porcentaje
                </Radio.Button>
                <Radio.Button value="monto_fijo">
                  <DollarOutlined /> Monto Fijo
                </Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              label={tipoDescuento === 'porcentaje' ? 'Porcentaje de Descuento' : 'Monto de Descuento'}
              name="valorDescuento"
              rules={[
                { required: true, message: 'Ingrese el valor del descuento' },
                { type: 'number', min: 1, message: 'El valor debe ser mayor a 0' },
                tipoDescuento === 'porcentaje' && { 
                  type: 'number', 
                  max: 100, 
                  message: 'El porcentaje no puede ser mayor a 100' 
                }
              ].filter(Boolean)}
            >
              {tipoDescuento === 'porcentaje' ? (
                <InputNumber
                  min={1}
                  max={100}
                  className="w-full"
                  suffix="%"
                  placeholder="20"
                />
              ) : (
                <InputNumber
                  min={1}
                  className="w-full"
                  prefix="$"
                  formatter={formatCLP}
                  parser={parseCLP}
                  placeholder="5.000"
                />
              )}
            </Form.Item>

            <Alert
              message={
                tipoDescuento === 'porcentaje'
                  ? 'Se aplicará automáticamente un descuento del porcentaje indicado sobre el precio del taller'
                  : 'Se restará automáticamente el monto fijo indicado del precio del taller'
              }
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
            />
          </Card>

          {/* Vigencia */}
          <Card 
            title={
              <span className="flex items-center gap-2">
                <CalendarOutlined /> Período de Vigencia
              </span>
            } 
            size="small" 
            className="mb-4"
          >
            <Form.Item
              label="Rango de Fechas y Horario"
              name="fechas"
              rules={[{ required: true, message: 'Seleccione las fechas de vigencia' }]}
              tooltip="El descuento se aplicará automáticamente durante este período"
            >
              <RangePicker
                className="w-full"
                showTime
                format="DD/MM/YYYY HH:mm"
                placeholder={['Fecha y hora inicio', 'Fecha y hora fin']}
              />
            </Form.Item>

            <Alert
              message="El descuento se aplicará automáticamente entre las fechas seleccionadas"
              description="Los usuarios verán el descuento reflejado automáticamente en el carrito durante este período."
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
            />
          </Card>

          {/* Talleres aplicables - 🔧 CORREGIDO */}
          <Card 
            title={
              <span className="flex items-center gap-2">
                <AppstoreOutlined /> Talleres Aplicables
              </span>
            } 
            size="small" 
            className="mb-4"
          >
            <Form.Item
              label="Seleccionar Talleres"
              name="talleresAplicables"
              rules={[
                { required: true, message: 'Seleccione al menos un taller' }
              ]}
              tooltip="Selecciona los talleres donde se aplicará este descuento automáticamente"
            >
              <Select
                mode="multiple"
                placeholder={talleresLoading ? "Cargando talleres..." : "Seleccione los talleres"}
                loading={talleresLoading}
                disabled={talleresLoading}
                notFoundContent={talleresLoading ? <Spin size="small" /> : "No hay talleres disponibles"}
                options={talleres.map(t => ({
                  label: `${t.nombre} - ${t.recinto?.nombre || 'Sin recinto'}`,
                  value: t._id
                }))}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                showSearch
                maxTagCount="responsive"
              />
            </Form.Item>

            {/* 🆕 Mostrar contador de talleres */}
            {talleres.length > 0 && (
              <Alert
                message={`${talleres.length} talleres disponibles para seleccionar`}
                description="Este descuento se aplicará automáticamente a los talleres seleccionados durante el período configurado."
                type="info"
                showIcon
              />
            )}

            {/* 🆕 Alerta si no hay talleres */}
            {!talleresLoading && talleres.length === 0 && (
              <Alert
                message="No hay talleres disponibles"
                description="Crea talleres primero antes de configurar descuentos."
                type="warning"
                showIcon
              />
            )}
          </Card>

          {/* Configuración avanzada */}
          <Card 
            title={
              <span className="flex items-center gap-2">
                <TrophyOutlined /> Configuración Avanzada
              </span>
            } 
            size="small" 
            className="mb-4"
          >
            <Form.Item
              label={
                <span className="flex items-center gap-2">
                  Prioridad de Aplicación
                  <Tag color={prioridad > 5 ? 'green' : prioridad > 2 ? 'blue' : 'default'}>
                    {prioridad > 5 ? 'Alta' : prioridad > 2 ? 'Media' : 'Baja'}
                  </Tag>
                </span>
              }
              name="prioridad"
              tooltip="Si hay múltiples descuentos vigentes, se aplicará primero el de mayor prioridad"
            >
              <Slider
                min={0}
                max={10}
                marks={prioridadMarks}
                value={prioridad}
                onChange={setPrioridad}
              />
            </Form.Item>

            <Form.Item
              label="Acumulación con otros descuentos"
              name="acumulableConOtrosDescuentos"
              valuePropName="checked"
              tooltip="Si está habilitado, este descuento puede aplicarse junto con otros descuentos de menor prioridad"
            >
              <Switch
                checkedChildren="Acumulable"
                unCheckedChildren="No acumulable"
              />
            </Form.Item>

            <Alert
              message="Prioridad y acumulación"
              description={
                <div className="space-y-1">
                  <div>• <strong>Prioridad:</strong> Los descuentos con mayor prioridad se aplican primero.</div>
                  <div>• <strong>Acumulable:</strong> Si está activado, permite que se apliquen múltiples descuentos simultáneamente.</div>
                  <div>• Si no es acumulable, solo se aplicará este descuento (el de mayor prioridad).</div>
                </div>
              }
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
            />
          </Card>

          {/* Preview del descuento */}
          <Card 
            title="Vista Previa" 
            size="small" 
            className="mb-4"
            style={{ backgroundColor: '#eff6ff', borderColor: '#bfdbfe' }}
          >
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <ThunderboltOutlined className="text-yellow-500 text-xl" />
                <Text strong className="text-base">
                  {form.getFieldValue('nombre') || 'Nombre del descuento'}
                </Text>
                <Tag color={tipoDescuento === 'porcentaje' ? 'blue' : 'green'}>
                  {tipoDescuento === 'porcentaje' 
                    ? `${form.getFieldValue('valorDescuento') || 0}%`
                    : `$${formatCLP(form.getFieldValue('valorDescuento') || 0)}`
                  }
                </Tag>
              </div>
              <Text type="secondary" className="text-sm">
                {form.getFieldValue('descripcion') || 'Descripción del descuento'}
              </Text>
              <div className="flex gap-2 flex-wrap mt-2">
                <Tag color="purple">Automático</Tag>
                <Tag color={prioridad > 5 ? 'green' : prioridad > 2 ? 'blue' : 'default'}>
                  Prioridad: {prioridad}
                </Tag>
                {form.getFieldValue('acumulableConOtrosDescuentos') && (
                  <Tag color="cyan">Acumulable</Tag>
                )}
              </div>
            </div>
          </Card>

          {/* Botones */}
          <Divider />
          <div className="flex justify-end gap-4">
            <Button
              size="large"
              onClick={handleCancel}
            >
              Cancelar
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              htmlType="submit"
              loading={saving}
              disabled={!talleresLoading && talleres.length === 0} // 🆕 Deshabilitar si no hay talleres
              style={{ backgroundColor: '#0047bb', borderColor: '#0047bb' }}
            >
              {isEditMode ? 'Guardar Cambios' : 'Crear Descuento Automático'}
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default DescuentoAutomaticoForm;
